import { useJobillaOrgId } from '@/hooks'
import type { StripeFeature, SubscriptionAccessData } from '@jobilla/nexus-sdk'
import {
	broadcastChannel,
	entitlementIndexQueryKey,
	subscriptionEvent,
	subscriptionIndexQueryKey,
	useEntitlementIndex,
} from '@jobilla/nexus-sdk'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { usePrivateChannel } from '../echo'

export interface CampaignQuota {
	usage: number
	limit: number | null
	periodEnd: string | null
	isNearLimit: boolean
	isExhausted: boolean
}

/**
 * Formats a billing period end date for display (e.g. "Mar 31").
 */
export function formatPeriodEnd(periodEnd: string | null): string | null {
	if (!periodEnd) { return null }
	return new Date(periodEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface EntitlementsContextValue {
	can: (feature: StripeFeature) => boolean
	entitlements: StripeFeature[]
	isLoading: boolean
	canSendToReview: boolean
	isFullyServiced: boolean
	campaignQuota: CampaignQuota
	/** Null until the first entitlements response arrives. */
	subscriptionAccess: SubscriptionAccessData | null
}

const EntitlementsContext = createContext<EntitlementsContextValue | undefined>(undefined)

export function EntitlementsProvider({ children }: { children: ReactNode }) {
	const organisationId = useJobillaOrgId()

	const { data, isLoading } = useEntitlementIndex(organisationId!, {
		enabled: !!organisationId,
		refetchOnWindowFocus: true,
		refetchInterval: 60_000,
		staleTime: 30_000,
	})

	usePrivateChannel(
		organisationId ? broadcastChannel.OrganisationSubscription({ organisation: organisationId }) : null,
		{ [subscriptionEvent.Changed]: [entitlementIndexQueryKey, subscriptionIndexQueryKey] },
	)

	const value = useMemo<EntitlementsContextValue>(() => {
		const entitlements = (data?.entitlements ?? []) as StripeFeature[]
		const enforced = data?.enforced ?? false

		const usage = data?.campaignUsage ?? 0
		const limit = data?.campaignLimit ?? null
		const percentage = limit ? (usage / limit) * 100 : null

		const campaignQuota: CampaignQuota = {
			usage,
			limit,
			periodEnd: data?.campaignPeriodEnd ?? null,
			isNearLimit: percentage !== null && percentage >= 80 && percentage < 100,
			isExhausted: percentage !== null && percentage >= 100,
		}

		return {
			// When enforcement is off, all features are unlocked.
			// Service tier is always enforced regardless of this flag.
			can: (feature: StripeFeature) => !enforced || entitlements.includes(feature),
			entitlements,
			isLoading,
			canSendToReview: data?.canSendToReview ?? false,
			isFullyServiced: data?.isFullyServiced ?? false,
			campaignQuota,
			subscriptionAccess: data?.subscriptionAccess ?? null,
		}
	}, [data, isLoading])

	return (
		<EntitlementsContext.Provider value={value}>
			{children}
		</EntitlementsContext.Provider>
	)
}

export function useEntitlements(): EntitlementsContextValue {
	const context = useContext(EntitlementsContext)

	if (context === undefined) {
		throw new Error('useEntitlements must be used within EntitlementsProvider')
	}

	return context
}
